import * as React from 'react';
import cx from 'clsx';

import { AccountLi } from '../../icons';
import { QuicksilverTileOutfitType } from '../types';

import styles from './LiveTableLayerInfo.module.scss';
import { Table } from '../../../types/gameTypes';

interface LiveTableLayerInfoProps {
  stake?: Table['stake'];
  players?: number;
  outfit: QuicksilverTileOutfitType;
  seats?: number;
  locale?: string;
  currencyCode?: string;
}

const GT_LT_LAYER_TEST_ATTR_INFO_CONTENT = 'gt-live-table-layer-info-content';
const GT_LT_LAYER_TEST_ATTR_INFO = 'gt-live-table-layer-info';
const GT_LT_LAYER_TEST_ATTR_INFO_ICON = 'gt-live-table-layer-info-icon';

const currencyFormatMinValueMapper: {
  [key: string]: {
    unit: string;
    multiplier: number;
    localization: 'before' | 'after';
  };
} = {
  GBP: {
    unit: 'p',
    multiplier: 100,
    localization: 'after',
  },
};

const formatCurrencyBasedOnLocale = ({
  value,
  locale = 'en-gb',
  currencyCode = 'GBP',
}: {
  value?: number;
  locale?: string;
  currencyCode?: string;
}) => {
  if (!value) return;
  const mapper = currencyFormatMinValueMapper[currencyCode];
  if (value < 1 && mapper) {
    return mapper.localization === 'before'
      ? `${mapper.unit}${value * mapper.multiplier}`
      : `${value * mapper.multiplier}${mapper.unit}`;
  }
  return value.toLocaleString(locale, {
    style: 'currency',
    currency: currencyCode,
    maximumSignificantDigits: value.toString().length,
    currencyDisplay: 'symbol',
  });
};

const prepareStakeText = (
  outfit: QuicksilverTileOutfitType,
  stake?: Table['stake'],
  locale?: string,
  currencyCode?: string
) => {
  if (!stake) return '';
  const formattedMin = formatCurrencyBasedOnLocale({
    value: stake.min,
    locale,
    currencyCode,
  });
  const formattedMax = formatCurrencyBasedOnLocale({
    value: stake.max,
    locale,
    currencyCode,
  });
  return (outfit === 'big' || outfit === 'banner') && formattedMax
    ? `${formattedMin} - ${formattedMax}`
    : outfit === 'compressed'
    ? `, Stake ${formattedMin}`
    : formattedMin;
};

const LiveTableLayerInfo = ({
  stake,
  players = 0,
  outfit,
  seats,
  locale,
  currencyCode,
}: LiveTableLayerInfoProps) => {
  const stakeText = prepareStakeText(outfit, stake, locale, currencyCode);
  const playersText = seats ? `${players}/${seats}` : players;
  const isTooManyPlayers = seats && players >= seats;

  return (
    <div
      className={cx(
        styles.container,
        outfit === 'compressed' && styles.containerCompressed
      )}
      data-testid={GT_LT_LAYER_TEST_ATTR_INFO}
    >
      {outfit !== 'compressed' && (
        <span className={styles.stake}>{stakeText}</span>
      )}
      <div className={styles.players}>
        <AccountLi
          className={cx(
            styles.infoIcon,
            seats && (isTooManyPlayers ? styles.iconRed : styles.iconGreen)
          )}
          data-testid={GT_LT_LAYER_TEST_ATTR_INFO_ICON}
        />
        <div
          className={cx(
            styles.text,
            seats && (isTooManyPlayers ? styles.textRed : styles.textGreen)
          )}
        >
          <span data-testid={GT_LT_LAYER_TEST_ATTR_INFO_CONTENT}>
            {playersText}
          </span>
          {outfit === 'compressed' && (
            <span className={styles.stake}>{stakeText}</span>
          )}
        </div>
      </div>
    </div>
  );
};

export default React.memo(LiveTableLayerInfo);
